'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  username?: string;
  displayName?: string;
  role?: string;
  isActive?: boolean;
  lastLogin?: string;
}

interface AuthContextValue {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  signIn: async () => {},
  signOut: async () => {},
  getCurrentUser: async () => null,
  isEmailVerified: () => false,
  getUserProfile: async () => null,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const loadProfile = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, username, display_name, role, is_active, last_login')
        .eq('id', userId)
        .maybeSingle();
      if (data) {
        setProfile({
          id: data.id,
          email: data.email,
          fullName: data.full_name ?? '',
          username: data.username ?? undefined,
          displayName: data.display_name ?? data.full_name ?? undefined,
          role: data.role ?? undefined,
          isActive: data.is_active ?? true,
          lastLogin: data.last_login ?? undefined,
        });
      }
    } catch {
      // silent
    }
  };

  useEffect(() => {
    // Initialize: use Supabase session as the single source of truth.
    // sessionStorage flag is NOT used to determine session validity.
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Mark session as active in sessionStorage (cleared on tab/window close)
    sessionStorage.setItem('ercomex_session_active', '1');
    return data;
  };

  const signOut = async () => {
    // Log logout
    try {
      if (user) {
        await supabase.from('user_audit_log').insert({
          usuario_id: user.id,
          usuario_nombre: profile?.displayName ?? user.email,
          accion: 'logout',
          modulo: 'auth',
          detalle: `Cierre de sesión: ${user.email}`,
        });
      }
    } catch { /* silent */ }
    sessionStorage.removeItem('ercomex_session_active');
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value: AuthContextValue = {
    user,
    session,
    profile,
    loading,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
